// "Your decision" — the first section of TaskDetailDialog while a task is awaiting_human (decision D23, item 2 of
// the approved change). The question, options, reply code and glossary used to live on the Kanban card; the card is
// now just a compact, whole-card-clickable entry (ApprovalColumn.tsx) and this is where the real decision happens,
// alongside the rest of the task.
//
// A paused/draining/stopped project would refuse an "approve" option server-side (it would send the task back into
// production): those options are disabled here with a notice, and a maintainer+ can resume the project from the
// same place. If the project resumes between load and click regardless (a race), the server's own project_halted
// refusal is shown as the same localized notice instead of the raw error.
import { useId, useState } from "react";
import type { ProjectSummary, TaskCard } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { isApiError } from "../../api/client.ts";
import { changeExecution } from "../../api/endpoints.ts";
import { decideApproval, listProjectApprovals } from "../../api/endpoints-wave5.ts";
import type { ApprovalOption } from "../../api/types-wave5.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { Button } from "../../components/Button.tsx";
import { TextAreaField } from "../../components/Field.tsx";
import { Banner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { useToast } from "../../components/Toast.tsx";
import { GrantFactsList } from "../assistant/GrantApprovalCard.tsx";

const HALTED_STATES = new Set<ProjectSummary["state"]>(["paused", "draining", "stopped"]);

export function ApprovalDecision({
  projectId,
  task,
  project,
  canResume,
  onDecided,
  onProjectChanged,
}: {
  projectId: string;
  task: TaskCard;
  project: ProjectSummary;
  canResume: boolean;
  onDecided: () => void;
  onProjectChanged: () => void;
}) {
  const { t } = useI18n();
  const { toast, announce } = useToast();
  const approvals = useAsync((signal) => listProjectApprovals(projectId, "pending", signal), [projectId, task.id, task.version]);
  const approval = (approvals.data?.items ?? []).find((a) => a.task_id === task.id) ?? null;

  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<unknown>(null);
  const noticeId = useId();

  const halted = HALTED_STATES.has(project.state);
  const errorDetails = isApiError(error) ? (error.details as { cause?: unknown; project_state?: unknown } | undefined) : undefined;
  const haltedByServer = error !== null && errorDetails?.cause === "project_halted";
  const effectiveHalted = halted || haltedByServer;
  const effectiveState = halted ? project.state : typeof errorDetails?.project_state === "string" ? errorDetails.project_state : project.state;

  const decide = async (option: ApprovalOption) => {
    if (!approval) return;
    setBusy(option.key);
    setError(null);
    try {
      const r = await decideApproval(approval.id, { option: option.key, ...(note.trim() ? { note: note.trim() } : {}) });
      const msg = t("approval.decided", { number: task.number, option: option.label });
      toast(msg, "success");
      announce(msg);
      if (r.task_run.error) toast(t("approval.runNotStarted", { reason: r.task_run.error }), "error");
      onDecided();
    } catch (e) {
      // Someone decided first (another person, WhatsApp or Telegram): nothing left to do here.
      if (isApiError(e) && e.status === 409 && e.code === "policy_denied") {
        toast(t("approval.alreadyDecided", { number: task.number }), "info");
        onDecided();
      } else setError(e);
    } finally {
      setBusy(null);
    }
  };

  const resume = async () => {
    setResumeBusy(true);
    setResumeError(null);
    try {
      await changeExecution(project.id, "resume", project.version);
      toast(t("approval.resumeSuccess"), "success");
      onProjectChanged();
      onDecided();
    } catch (e) {
      setResumeError(e);
    } finally {
      setResumeBusy(false);
    }
  };

  return (
    <section className="approval-decision" data-testid="approval-decision">
      <h3 className="detail-heading">{t("approval.yourDecision")}</h3>
      {approvals.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : !approval ? (
        <p className="subtle" data-testid="approval-missing">
          {t("approval.missing")}
        </p>
      ) : (
        <>
          <p className="approval-question" data-testid="approval-question">
            {approval.question}
          </p>
          {effectiveHalted ? (
            <Banner tone="warning" role="status" id={noticeId} testId="approval-halted-notice">
              {t(`approval.haltedNotice.${effectiveState}` as "approval.haltedNotice.paused")}
              {canResume ? (
                <div className="row row--wrap" style={{ marginTop: 6 }}>
                  <Button size="sm" onClick={() => void resume()} loading={resumeBusy} disabled={resumeBusy} data-testid="approval-resume">
                    {t("approval.resumeProject")}
                  </Button>
                </div>
              ) : null}
            </Banner>
          ) : null}
          <ActionError error={resumeError} onDismiss={() => setResumeError(null)} />
          {/* D41-C4: a service_grant request shows what it allows before its answers. */}
          {approval.kind === "service_grant" ? <GrantFactsList approval={approval} /> : null}
          <div className="approval-options" role="group" aria-label={t("approval.options")}>
            {approval.options.map((o) => {
              const recommended = o.key === approval.recommended;
              const disabledByHalt = effectiveHalted && o.effect === "approve";
              return (
                <Button
                  key={o.key}
                  size="sm"
                  variant={recommended ? "primary" : "secondary"}
                  className="approval-option"
                  loading={busy === o.key}
                  disabled={busy !== null || disabledByHalt}
                  aria-describedby={disabledByHalt ? noticeId : undefined}
                  onClick={() => void decide(o)}
                  data-testid="approval-option"
                  data-effect={o.effect}
                  data-recommended={recommended || undefined}
                >
                  <span className="mono">{o.key}</span>
                  <span>{o.label}</span>
                  {recommended ? <span className="approval-recommended">{t("approval.recommended")}</span> : null}
                </Button>
              );
            })}
          </div>
          <p className="field-hint" data-testid="approval-code">
            {t("approval.replyCode")} <span className="badge mono">{approval.code}</span>{" "}
            {t("approval.replyHint", { example: `${approval.code} ${approval.recommended ?? approval.options[0]?.key ?? "1"}` })}
          </p>
          {approval.glossary.length > 0 ? (
            <details className="disclosure">
              <summary>{t("approval.glossary")}</summary>
              <dl data-testid="approval-glossary">
                {approval.glossary.map((g) => (
                  <div key={g.term}>
                    <dt>{g.term}</dt>
                    <dd>{g.explanation}</dd>
                  </div>
                ))}
              </dl>
            </details>
          ) : null}
          <TextAreaField label={t("approval.note")} optionalLabel={t("common.optional")} rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
          {/* The raw refusal is only shown for errors other than the halted-project race: that one gets the same
              localized notice above instead (already rendered from `effectiveHalted`). */}
          <ActionError error={haltedByServer ? null : error} onDismiss={() => setError(null)} />
        </>
      )}
    </section>
  );
}
